import { useId } from "react";

// Small Visa + Mastercard acceptance tiles, shown directly under the "Payment"
// header on the customer checkout (cards are taken via Verifone; Visa + Mastercard
// are the accepted types). Visual signalling only — decorative, not interactive.
//
// Each brand mark is inline SVG (Visa wordmark / Mastercard interlocking circles)
// so it stays crisp at any size with no extra network request, sat inside a small
// light tile that matches the design-system surface / radius / hairline-border
// tokens already used on the checkout screen — quiet and subtle, never oversized.

const tileCls = "flex h-6 w-9 items-center justify-center rounded border border-ink/10 bg-cream";

function VisaTile() {
  return (
    <span className={tileCls}>
      {/* Visa wordmark */}
      <svg viewBox="0 0 24 24" role="img" aria-label="Visa" className="h-3.5 w-auto" fill="#1434CB">
        <path d="M9.112 8.262L5.97 15.758H3.92L2.374 9.775c-.094-.368-.175-.503-.461-.658C1.447 8.864.677 8.627 0 8.479l.046-.217h3.3a.904.904 0 01.894.764l.817 4.338 2.018-5.102zm8.033 5.049c.008-1.979-2.736-2.088-2.717-2.972.006-.269.262-.555.822-.628a3.66 3.66 0 011.913.336l.34-1.59a5.207 5.207 0 00-1.814-.333c-1.917 0-3.266 1.018-3.278 2.477-.012 1.079.963 1.68 1.698 2.04.756.367 1.01.603 1.006.931-.005.504-.602.726-1.16.734-.975.015-1.54-.263-1.992-.473l-.351 1.642c.453.208 1.289.39 2.156.398 2.037 0 3.37-1.006 3.376-2.564m5.061 2.447H24l-1.565-7.496h-1.656a.883.883 0 00-.826.55l-2.909 6.946h2.036l.405-1.12h2.488zm-2.163-2.656l1.02-2.815.588 2.815zm-8.426-4.84l-1.603 7.496H8.13l1.604-7.496z" />
      </svg>
    </span>
  );
}

function MastercardTile() {
  // Unique per instance so the two render sites don't collide on the clip-path id.
  // useId() contains colons, which are awkward in an SVG url(#…) reference — strip
  // them to a plain alphanumeric id.
  const clipId = `mc${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  return (
    <span className={tileCls}>
      {/* Mastercard interlocking circles; the overlap is the right circle clipped to the left */}
      <svg viewBox="0 0 32 20" role="img" aria-label="Mastercard" className="h-4 w-auto">
        <circle cx="12" cy="10" r="7" fill="#EB001B" />
        <circle cx="20" cy="10" r="7" fill="#F79E1B" />
        <clipPath id={clipId}>
          <circle cx="12" cy="10" r="7" />
        </clipPath>
        <circle cx="20" cy="10" r="7" fill="#FF5F00" clipPath={`url(#${clipId})`} />
      </svg>
    </span>
  );
}

export function AcceptedCards({ className }: { className?: string }) {
  return (
    <div className={["flex items-center gap-2", className].filter(Boolean).join(" ")}>
      <VisaTile />
      <MastercardTile />
    </div>
  );
}
